import { useI18n } from '@/i18n'
import { X } from '@/shared/icons/phosphor'
import { For, Show, createSignal, createUniqueId } from 'solid-js'
import { OptionalBadge } from '../../primitives/Badge/OptionalBadge'
import { FIELD_MESSAGE_METRICS, FieldError } from '../../primitives/FieldError'

export interface TagInputProps {
  value: string[]
  onChange: (next: string[]) => void
  label?: string
  optional?: boolean
  placeholder?: string
  hint?: string
  error?: string
  id?: string
  disabled?: boolean
  flushRef?: (flush: () => void) => void
  class?: string
}

export function TagInput(props: TagInputProps) {
  const { t } = useI18n()
  const [draft, setDraft] = createSignal('')
  const uid = createUniqueId()
  const fieldId = () => props.id ?? `taginput-${uid}`
  const hintId = () => `${fieldId()}-hint`
  const errorId = () => `${fieldId()}-error`
  const showsHint = () => Boolean(props.hint) && !props.error
  const describedBy = () =>
    [props.error ? errorId() : null, showsHint() ? hintId() : null].filter(Boolean).join(' ') ||
    undefined
  let field: HTMLInputElement | undefined

  const commit = (text: string) => {
    const next = [...props.value]
    for (const entry of text.split(',')) {
      const tag = entry.trim()
      const known = next.some(
        (existing) => existing.toLocaleLowerCase() === tag.toLocaleLowerCase()
      )
      if (tag && !known) {
        next.push(tag)
      }
    }
    if (next.length !== props.value.length) {
      props.onChange(next)
    }
  }

  const flush = () => {
    commit(draft())
    setDraft('')
  }

  props.flushRef?.(flush)

  const handleInput = (element: HTMLInputElement) => {
    const lastComma = element.value.lastIndexOf(',')
    if (lastComma === -1) {
      setDraft(element.value)
      return
    }
    commit(element.value.slice(0, lastComma))
    const remainder = element.value.slice(lastComma + 1).trimStart()
    setDraft(remainder)
    element.value = remainder
  }

  const remove = (tag: string) => {
    props.onChange(props.value.filter((existing) => existing !== tag))
    field?.focus()
  }

  return (
    <div class={`flex flex-col gap-1.5 ${props.class ?? ''}`.trim()}>
      <Show when={props.label}>
        <label
          for={fieldId()}
          class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]"
        >
          {props.label}
          <Show when={props.optional}>
            <OptionalBadge />
          </Show>
        </label>
      </Show>
      <div
        class={`ds-field flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-[var(--radius-12)] border bg-[var(--color-surface)] px-2 py-[5px] transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)] ${
          props.error
            ? 'border-[var(--color-error)] has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-[var(--color-error-600)]'
            : 'border-[var(--color-field-border)] hover:border-[var(--color-field-hover)] focus-within:border-[var(--color-primary-400)] focus-within:shadow-[var(--focus-ring)]'
        } ${props.disabled ? 'opacity-50' : ''}`}
      >
        <For each={props.value}>
          {(tag) => (
            <span class="inline-flex items-center gap-0.5 rounded-[var(--radius-capsule)] bg-[var(--color-neutral-100)] py-0.5 pr-0.5 pl-2.5 text-[length:var(--text-sm)] leading-6 text-[color:var(--color-text)]">
              {tag}
              <button
                type="button"
                class="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-capsule)] text-[color:var(--color-text-muted)] transition-colors hover:bg-[var(--color-neutral-200)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)] disabled:cursor-not-allowed"
                aria-label={t('designSystem.tagInput.remove', { tag })}
                disabled={props.disabled}
                onClick={() => remove(tag)}
              >
                <X class="h-3 w-3" />
              </button>
            </span>
          )}
        </For>
        <input
          ref={field}
          id={fieldId()}
          type="text"
          autocomplete="off"
          value={draft()}
          placeholder={props.value.length === 0 ? props.placeholder : undefined}
          disabled={props.disabled}
          aria-describedby={describedBy()}
          aria-invalid={props.error ? true : undefined}
          class="h-7 min-w-[8ch] flex-1 border-none bg-transparent px-1.5 font-[family-name:var(--font-sans)] text-[length:var(--text-base)] text-[color:var(--color-text)] placeholder:text-[color:var(--color-text-subtle)] focus:outline-none disabled:cursor-not-allowed"
          onInput={(event) => handleInput(event.currentTarget)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.isComposing) {
              event.preventDefault()
              flush()
            }
          }}
          onBlur={flush}
        />
      </div>
      <Show when={props.error}>
        <FieldError id={errorId()}>{props.error}</FieldError>
      </Show>
      <Show when={showsHint()}>
        <p id={hintId()} class={`${FIELD_MESSAGE_METRICS} text-[color:var(--color-text-muted)]`}>
          {props.hint}
        </p>
      </Show>
    </div>
  )
}
